import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import type { RouteParams } from 'vue-router'
import { useLocalStorage } from '@vueuse/core'
import { LocalStorage } from '../constants/local-storage'
import { useOrganizationsStore } from '~/store/organizations'
import { useWorkspacesStore } from '~/store/workspaces'
import { AppRoutes, OrganizationRoutes } from '~/constants/routes'

export const initializeMandatoryData = () => {
  const organizationsStore = useOrganizationsStore()
  const workspacesStore = useWorkspacesStore()
  const { organizations, selectedOrganization }
    = storeToRefs(organizationsStore)
  const { workspaces } = storeToRefs(workspacesStore)

  const lastOrganizationURLPath = ref('')
  const lastWorkspaceURLPath = ref('')
  const lastOrganizationId = useLocalStorage(LocalStorage.Organizations.LastVisited, '')

  const setMandatoryInformation = async (params: RouteParams) => {
    if (!(await getCurrentUser())) return

    if (!params.organizationURLPath) {
      if (lastOrganizationURLPath.value) {
        lastOrganizationURLPath.value = ''
        // TODO: Remove timeout but to fix the issue, it needs to be reset only once page is leaved when there was an `organizationURLPath` param because it breaks components
        setTimeout(() => {
          organizationsStore.setSelectedOrganization(null)
        }, 300)
        // TODO: Reset organizations
      }

      if (lastWorkspaceURLPath.value) {
        lastWorkspaceURLPath.value = ''
        // TODO: Reset organizations & workspaces values
      }
      return
    }

    // Manage Organization Selection
    if (lastOrganizationURLPath.value !== params.organizationURLPath) {
      // TODO: Trigger only once, when user is connected
      await organizationsStore.getOrganizations()

      const organizationToSelect = organizations.value.find(
        organization => organization.urlPath === params.organizationURLPath,
      )
      if (organizationToSelect) {
        organizationsStore.setSelectedOrganization(organizationToSelect)
        lastOrganizationURLPath.value = params.organizationURLPath as string
        lastOrganizationId.value = organizationToSelect.id

        // Fetch workspaces on organization change
        await workspacesStore.getWorkspaces()
      }
      else if (params.organizationURLPath) {
        navigateTo(AppRoutes.SelectOrganization)
      }
    }

    if (selectedOrganization.value) {
      if (!params.workspaceURLPath) {
        lastWorkspaceURLPath.value = ''
        // workspacesStore.setSelectedWorkspace(null)
        return
      }

      // Manage Workspace Selection
      if (
        params.workspaceURLPath
        && lastWorkspaceURLPath.value !== params.workspaceURLPath
      ) {
        const workspaceToSelect = workspaces.value.find(
          workspace =>
            workspace.workspace.urlPath === params.workspaceURLPath,
        )

        if (workspaceToSelect) {
          workspacesStore.setSelectedWorkspace(workspaceToSelect)
          lastWorkspaceURLPath.value = params.workspaceURLPath as string
        }
        else {
          navigateTo(getOrganizationRoutePath(OrganizationRoutes.Base))
        }
      }
    }
  }

  const { beforeEach } = useRouter()
  beforeEach(async (to) => {
    await setMandatoryInformation(to.params)
  })

  return { setMandatoryInformation }
}
